import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Skeleton,
  Divider,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  getProductDetailsById,
  getSubCategory,
  uploadImage
} from '../../services/user';
import RenderInput from '../../utils/RandarInput';
import useForm from '../../hooks/useForm';
import BackNavigationButton from '../Shared/BackNavigationButton';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { countries } from '../../utils/countries';

const API_URL = 'https://seller.udyamwell.in/api';
const UPLOAD_API_URL = `${API_URL}/upload`;

const GST_OPTIONS = [
  { key: "0%", value: "0" },
  { key: "5%", value: "5" },
  { key: "12%", value: "12" },
  { key: "18%", value: "18" },
  { key: "28%", value: "28" },
];

const UOM_OPTIONS = [
  { key: "unit", value: "unit" },
  { key: "dozen", value: "dozen" },
  { key: "gram", value: "gram" },
  { key: "kilogram", value: "kilogram" },
  { key: "tonne", value: "tonne" },
  { key: "litre", value: "litre" },
  { key: "millilitre", value: "millilitre" },
];

const GENDER_OPTIONS = [
  { key: "Male", value: "Male" },
  { key: "Female", value: "Female" },
  { key: "Unisex", value: "Unisex" },
  { key: "Kids", value: "Kids" },
];

const STEPS = [
  'Basic Info',
  'Pricing & Inventory',
  'Logistics & Shipping',
  'Statutory & Mfr',
  'Category Specific',
  'Images'
];

const EditProduct: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [fetchingSubcategories, setFetchingSubcategories] = useState(false);
  const [rawSubcategoryData, setRawSubcategoryData] = useState<any[]>([]);
  const [extraOptions, setExtraOptions] = useState<any>({ colors: [], materials: [] });
  const [activeStep, setActiveStep] = useState(0);

  const { formValues, setFormValues, errors, setErrors, handleInputChange } = useForm({
    productName: '',
    category: '',
    productSubcategory: '',
    sku: '',
    price: '',
    maxPrice: '',
    quantity: '',
    maxAllowedQty: '',
    itemCondition: '',
    msnCode: '',
    productCode: '',
    productDescription: '',
    featureHighlights: '',
    additionalHighlights: '',
    GST_Percentage: '',
    UOM: '',
    UOMvalue: '',
    isCancellable: false,
    availableOnCod: false,
    returnWindow: '',
    manufacturedDate: '',
    manufacturerOrPackerName: '',
    manufacturerOrPackerAddress: '',
    length: '',
    breadth: '',
    height: '',
    weight: '',
    backImage: '',
    productImage: [] as string[],
    // Grocery
    fssaiCertificate: '',
    nutritionalInfo: '',
    isVegetarian: false,
    brandOwnerFSSAILicenseNo: '',
    importerFSSAILicenseNo: '',
    // Fashion
    colour: '',
    colourName: '',
    brand: '',
    size: '',
    gender: '',
    fabric: '',
    sizeChart: '',
    // Home & Kitchen
    material: '',
    strapMaterial: '',
    waterResistant: '',
    display: '',
    glassMaterial: '',
    sportType: '',
    baseMetal: '',
    planting: '',
  });

  const fetchSubcategories = useCallback(async (category: string) => {
    if (!category) return;
    try {
      setFetchingSubcategories(true);
      const data = await getSubCategory({ category });

      // Robustly handle different API structures
      // Case 1: Data is directly the array (Home & Kitchen / Fashion)
      // Case 2: Data is nested under category.data (Grocery)
      const subcategoryList = Array.isArray(data) ? data : (data?.category?.data || data?.data || []);
      setRawSubcategoryData(subcategoryList);

      // Store all potential dropdown lists from the API (top level)
      setExtraOptions({
        colors: data?.colors || [],
        materials: data?.material || [],
        base_metal: data?.base_metal || [],
        display: data?.display || [],
        fabric: data?.fabric || [],
        gender: data?.gender || [],
        glass_material: data?.glass_material || [],
        plating: data?.plating || [],
        sport_type: data?.sport_type || [],
        strap_material: data?.strap_material || [],
      });

      const options = subcategoryList.map((sub: any) => ({
        key: sub.name,
        value: sub.key || sub.name
      }));
      setSubcategories(options);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      toast.error('Failed to fetch subcategories');
    } finally {
      setFetchingSubcategories(false);
    }
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await getProductDetailsById(id);
        const product = response.data;

        const catLower = (product.category || product.productCategory || '').toString().toLowerCase();
        const isFashionOrHome = catLower.includes('fashion') || catLower.includes('home');

        setFormValues((prev: any) => ({
          ...prev,
          ...product,
          GST_Percentage: product.GST_Percentage !== undefined ? String(product.GST_Percentage) : '',
          UOM: isFashionOrHome ? 'unit' : (product.UOM || ''),
          isCancellable: !!product.isCancellable,
          availableOnCod: !!product.availableOnCod,
          isVegetarian: !!product.isVegetarian,
          productImage: Array.isArray(product.productImage) ? product.productImage : [],
        }));

        if (product.category || product.category) {
          fetchSubcategories(product.category || product.category);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        toast.error('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, setFormValues, fetchSubcategories]);

  const handleCategoryChange = (newCategory: string) => {
    const isFashionOrHome = (newCategory || '').toLowerCase().includes('fashion') || (newCategory || '').toLowerCase().includes('home');
    const clearedFields: any = {
      category: newCategory,
      UOM: isFashionOrHome ? 'unit' : '',
      productSubcategory: '',
      fssaiCertificate: '',
      nutritionalInfo: '',
      isVegetarian: false,
      brandOwnerFSSAILicenseNo: '',
      importerFSSAILicenseNo: '',
      colour: '',
      colourName: '',
      brand: '',
      size: '',
      gender: '',
      fabric: '',
      sizeChart: '',
      material: '',
      strapMaterial: '',
      waterResistant: '',
      display: '',
      glassMaterial: '',
      sportType: '',
      baseMetal: '',
      planting: ''
    };

    setFormValues((prev: any) => ({
      ...prev,
      ...clearedFields
    }));

    fetchSubcategories(newCategory);
  };

  const handleFileUpload = async (files: FileList | null, fieldId: string) => {
    if (!files || files.length === 0) return;

    const toastId = toast.loading('Uploading images...');

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
          const response = await axios.post(UPLOAD_API_URL, formData);
          // The API returns the URL directly in the 'data' property as a string
          if (response.data.success && typeof response.data.data === 'string') {
            return response.data.data;
          }
          // Fallback for other structures
          return response.data.url || response.data.data?.url;
        } catch (err) {
          console.error(`Upload failed for ${file.name}`, err);
          return null;
        }
      });

      const urls = await Promise.all(uploadPromises);
      const validUrls = urls.filter(url => typeof url === 'string' && url.length > 0);

      if (validUrls.length === 0) {
        toast.error('Upload failed. Please try again.', { id: toastId });
        return;
      }

      if (fieldId === 'productImage') {
        setFormValues((prev: any) => ({
          ...prev,
          productImage: [...(prev.productImage || []), ...validUrls]
        }));
      } else {
        setFormValues((prev: any) => ({
          ...prev,
          [fieldId]: validUrls[0]
        }));
      }

      // Reset the file input so the same file can be uploaded again if needed
      const inputId = fieldId === 'productImage' ? 'productImageInput' : (fieldId === 'backImage' ? 'backImageInput' : 'sizeChartInput');
      const input = document.getElementById(inputId) as HTMLInputElement;
      if (input) input.value = '';

      toast.success('Upload successful!', { id: toastId });
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('An error occurred during upload', { id: toastId });
    }
  };

  const removeImage = (index: number) => {
    setFormValues((prev: any) => ({
      ...prev,
      productImage: prev.productImage.filter((_: any, i: number) => i !== index)
    }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If not on the last step, advance to the next step
    if (activeStep < STEPS.length - 1) {
      handleNext();
      return;
    }

    if (!id) {
      toast.error('Product ID is missing');
      return;
    }

    if (formValues.productImage.length < 3) {
      toast.error('Minimum 3 images are required');
      return;
    }

    try {
      setSubmitting(true);
      const payload: any = { productId: id };

      // Fields to explicitly exclude from the payload
      const restrictedFields = ['_id', 'published', 'status', 'id', 'rating', '__v', 'createdAt', 'updatedAt'];

      Object.keys(formValues).forEach(key => {
        // Skip restricted fields and the ID itself
        if (restrictedFields.includes(key) || key === 'productId') return;

        const value = formValues[key];
        if (value !== '' && value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            if (value.length > 0) payload[key] = value;
          } else {
            payload[key] = value;
          }
        }
      });

      payload.userId = localStorage.getItem('ud-user');
      payload.isUdyamWellHandleShipping = formValues.isUdyamWellHandleShipping || false;

      const response = await axios.put(`${API_URL}/product-update`, payload);

      if (response.data.success) {
        toast.success('Product updated successfully');
        navigate('/products');
      } else {
        toast.error(response.data.message || 'Update failed');
      }
    } catch (err: any) {
      console.error('Error updating product:', err);
      toast.error(err.response?.data?.message || 'Failed to update product');
    } finally {
      setSubmitting(false);
    }
  };

  const renderSectionFields = (fields: any[]) => (
    <Grid container spacing={2}>
      {fields.map(f => (
        <Grid size={{ xs: 12, md: f.fullWidth ? 12 : 6 }} key={f.id}>
          {f.type === 'toggle' ? (
            <FormControlLabel
              control={
                <Switch
                  checked={formValues[f.id]}
                  onChange={(e) => setFormValues((prev: any) => ({ ...prev, [f.id]: e.target.checked }))}
                />
              }
              label={f.title}
            />
          ) : f.type === 'custom-component' ? (
            <Box>{f.component}</Box>
          ) : (
            <RenderInput
              item={{
                ...f,
                options: f.id === 'productSubcategory' ? subcategories : f.options
              }}
              state={formValues}
              stateHandler={(newStateOrUpdater: any) => {
                if (typeof newStateOrUpdater === 'function') {
                  setFormValues((prev: any) => {
                    const newState = newStateOrUpdater(prev);
                    if (f.id === 'category' && newState.category !== prev.category) {
                      setTimeout(() => handleCategoryChange(newState.category), 0);
                    }
                    return newState;
                  });
                } else {
                  if (f.id === 'category' && newStateOrUpdater.category !== (formValues.category)) {
                    handleCategoryChange(newStateOrUpdater.category);
                  } else {
                    setFormValues(newStateOrUpdater);
                  }
                }
              }}
            />
          )}
        </Grid>
      ))}
    </Grid>
  );

  const category = formValues.category?.toLowerCase();

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            {renderSectionFields([
              { id: 'productName', title: 'Product Name', type: 'input' },
              {
                id: 'category',
                title: 'Product Category',
                type: 'select',
                isDisabled: true,
                options: [
                  { key: 'Grocery', value: 'Grocery' },
                  { key: 'Fashion', value: 'Fashion' },
                  { key: 'Home & Kitchen', value: 'Home & Kitchen' },
                ]
              },
              {
                id: 'productSubcategory',
                title: 'Product Subcategory',
                type: 'select',
                isDisabled: fetchingSubcategories,
                placeholder: fetchingSubcategories ? 'Loading subcategories...' : 'Select Subcategory'
              },
              { id: 'sku', title: 'SKU', type: 'input' },
              { id: 'productCode', title: 'Product Code', type: 'input' },
              { id: 'productDescription', title: 'Product Description', type: 'input', multiline: true, fullWidth: true, minRows: 6 },
              { id: 'featureHighlights', title: 'Feature Highlights', type: 'input', multiline: true, fullWidth: true, minRows: 3 },
              { id: 'additionalHighlights', title: 'Additional Feature', type: 'input', multiline: true, fullWidth: true, minRows: 3 },
            ])}
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Pricing & Inventory</Typography>
            {renderSectionFields([
              { id: 'price', title: 'Price', type: 'number' },
              { id: 'maxPrice', title: 'Max Price (MRP)', type: 'number' },
              { id: 'quantity', title: 'Quantity', type: 'number' },
              { id: 'maxAllowedQty', title: 'Max Allowed Quantity', type: 'number' },
            ])}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Logistics & Shipping</Typography>
            {renderSectionFields([
              { id: 'length', title: 'Length', type: 'number' },
              { id: 'breadth', title: 'Breadth', type: 'number' },
              { id: 'height', title: 'Height', type: 'number' },
              { id: 'weight', title: 'Weight', type: 'number' },
              { id: 'isCancellable', title: 'Is Cancellable', type: 'toggle' },
              { id: 'availableOnCod', title: 'Available on COD', type: 'toggle' },
            ])}
          </Box>
        );
      case 3:
        const currentCategory = (formValues.category || formValues.productCategory || '').toString().toLowerCase();
        const isFashionOrHome = currentCategory.includes('fashion') || currentCategory.includes('home');
        const activeUomOptions = isFashionOrHome
          ? [{ key: 'unit', value: 'unit' }]
          : UOM_OPTIONS;

        return (
          <Box>
            <Typography variant="h6" gutterBottom>Statutory & Manufacturer</Typography>
            {renderSectionFields([
              { id: 'manufacturerOrPackerName', title: 'Manufacturer/Packer Name', type: 'input' },
              { id: 'GST_Percentage', title: 'GST Percentage', type: 'select', options: GST_OPTIONS },
              { id: 'manufacturerOrPackerAddress', title: 'Manufacturer/Packer Address', type: 'input', multiline: true, fullWidth: true },
              { id: 'UOM', title: 'UOM', type: 'select', options: activeUomOptions },
              { id: 'UOMvalue', title: 'UOM Value', type: 'input' },
            ])}
          </Box>
        );
      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Category Specific Details ({formValues.category || formValues.category || 'Select Category First'})</Typography>
            {!(formValues.category || formValues.category) && <Typography color="error">Please select a product category in the first step.</Typography>}
            {category === 'grocery' && renderSectionFields([
              // { id: 'fssaiCertificate', title: 'FSSAI Certificate', type: 'input' },
              { id: 'isVegetarian', title: 'Is Vegetarian', type: 'toggle', fullWidth: true },
              { id: 'nutritionalInfo', title: 'Nutritional Info', type: 'input', multiline: true, fullWidth: true },
              { id: 'brandOwnerFSSAILicenseNo', title: 'Brand Owner FSSAI License No', type: 'input' },
              { id: 'importerFSSAILicenseNo', title: 'Importer FSSAI License No', type: 'input' },
            ])}
            {category === 'fashion' && (() => {
              if (fetchingSubcategories) {
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                    <CircularProgress size={40} sx={{ color: '#22c55e', mb: 2 }} />
                    <Typography color="textSecondary">Fetching category details...</Typography>
                  </Box>
                );
              }

              const selectedSub = rawSubcategoryData.find(s => (s.key || s.name) === formValues.productSubcategory);
              if (!selectedSub || !selectedSub.fields) {
                return (
                  <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                      Please select a valid subcategory in Step 1 to see specific fields.
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      (Selected: {typeof formValues.productSubcategory === 'string' ? formValues.productSubcategory : 'None'})
                    </Typography>
                  </Box>
                );
              }

              const mapOption = (opt: any) => {
                if (typeof opt === 'string') return { key: opt, value: opt };
                if (opt && typeof opt === 'object') {
                  return { key: String(opt.name || opt.key || opt.value || JSON.stringify(opt)), value: String(opt.value || opt.key || opt.name || JSON.stringify(opt)) };
                }
                return { key: String(opt), value: String(opt) };
              };

              const dynamicFields = selectedSub.fields
                .filter((f: any) => f.key !== 'colourName')
                .map((f: any) => {
                  if (f.key === 'sizeChart') {
                    return {
                      id: 'sizeChart',
                      title: 'Size Chart',
                      type: 'custom-component',
                      fullWidth: true,
                      component: (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: '#4b5563' }}>Size Chart</Typography>
                          <Box
                            sx={{
                              border: '1px dashed #ccc',
                              p: 2,
                              borderRadius: 1,
                              textAlign: 'center',
                              cursor: 'pointer',
                              bgcolor: '#f9f9f9',
                              '&:hover': { borderColor: '#22c55e', bgcolor: '#f0fdf4' }
                            }}
                            onClick={() => document.getElementById('sizeChartInput')?.click()}
                          >
                            <Typography variant="body2" color="textSecondary">
                              {formValues.sizeChart ? 'Change Size Chart' : 'Upload Size Chart'}
                            </Typography>
                            <input
                              id="sizeChartInput"
                              type="file"
                              hidden
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileUpload(e.target.files, 'sizeChart')}
                            />
                          </Box>
                          {formValues.sizeChart && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <img src={formValues.sizeChart} alt="Size Chart" style={{ height: 60, borderRadius: 4 }} />
                              <Button type="button" size="small" color="error" onClick={() => setFormValues((prev: any) => ({ ...prev, sizeChart: '' }))}>Remove</Button>
                            </Box>
                          )}
                        </Box>
                      )
                    };
                  }

                  const field: any = {
                    id: f.key,
                    title: f.name,
                    required: f.required,
                    type: f.type === 'select' ? 'select' : 'input'
                  };

                  // Map dynamic dropdowns from extraOptions
                  const optionMap: any = {
                    colour: extraOptions.colors,
                    material: extraOptions.materials,
                    baseMetal: extraOptions.base_metal,
                    display: extraOptions.display,
                    fabric: extraOptions.fabric,
                    gender: extraOptions.gender?.length > 0 ? extraOptions.gender : GENDER_OPTIONS.map(o => o.value),
                    glassMaterial: extraOptions.glass_material,
                    plating: extraOptions.plating,
                    sportType: extraOptions.sport_type,
                    strapMaterial: extraOptions.strap_material,
                  };

                  if (optionMap[f.key]) {
                    const list = optionMap[f.key];
                    field.options = (Array.isArray(list) ? list : []).map(mapOption);
                    if (field.options.length > 0) field.type = 'select';
                  }

                  return field;
                });

              return (
                <Grid container spacing={2}>
                  {dynamicFields.map((f: any) => (
                    <Grid size={{ xs: 12, md: 6 }} key={f.id}>
                      {f.type === 'custom-component' ? f.component : (
                        <RenderInput
                          item={f}
                          state={formValues}
                          stateHandler={(newStateOrUpdater: any) => {
                            if (typeof newStateOrUpdater === 'function') {
                              setFormValues((prev: any) => {
                                const newState = newStateOrUpdater(prev);
                                if (f.id === 'colour' && newState.colour !== prev.colour) {
                                  newState.colourName = newState.colour;
                                }
                                return newState;
                              });
                            } else {
                              const newState = { ...newStateOrUpdater };
                              if (f.id === 'colour' && newState.colour !== formValues.colour) {
                                newState.colourName = newState.colour;
                              }
                              setFormValues(newState);
                            }
                          }}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              );
            })()}
            {category === 'home & kitchen' && (() => {
              if (fetchingSubcategories) {
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                    <CircularProgress size={40} sx={{ color: '#22c55e', mb: 2 }} />
                    <Typography color="textSecondary">Fetching category details...</Typography>
                  </Box>
                );
              }
              const selectedSub = rawSubcategoryData.find(s => (s.key || s.name) === formValues.productSubcategory);
              if (!selectedSub || !selectedSub.fields) {
                return (
                  <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                      Please select a valid subcategory in Step 1 to see specific fields.
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      (Selected: {typeof formValues.productSubcategory === 'string' ? formValues.productSubcategory : 'None'})
                    </Typography>
                  </Box>
                );
              }

              const mapOption = (opt: any) => {
                if (typeof opt === 'string') return { key: opt, value: opt };
                if (opt && typeof opt === 'object') {
                  return { key: String(opt.name || opt.key || opt.value || JSON.stringify(opt)), value: String(opt.value || opt.key || opt.name || JSON.stringify(opt)) };
                }
                return { key: String(opt), value: String(opt) };
              };

              const dynamicFields = selectedSub.fields
                .filter((f: any) => f.key !== 'colourName')
                .map((f: any) => {
                  const field: any = {
                    id: f.key,
                    title: f.name,
                    required: f.required,
                    type: f.type === 'select' ? 'select' : 'input'
                  };

                  // Map dynamic dropdowns from extraOptions
                  const optionMap: any = {
                    colour: extraOptions.colors,
                    material: extraOptions.materials,
                    baseMetal: extraOptions.base_metal,
                    display: extraOptions.display,
                    fabric: extraOptions.fabric,
                    gender: extraOptions.gender,
                    glassMaterial: extraOptions.glass_material,
                    plating: extraOptions.plating,
                    sportType: extraOptions.sport_type,
                    strapMaterial: extraOptions.strap_material,
                  };

                  if (optionMap[f.key]) {
                    const list = optionMap[f.key];
                    field.options = (Array.isArray(list) ? list : []).map(mapOption);
                    if (field.options.length > 0) field.type = 'select';
                  }

                  return field;
                });

              return (
                <Grid container spacing={2}>
                  {dynamicFields.map((f: any) => (
                    <Grid size={{ xs: 12, md: 6 }} key={f.id}>
                      <RenderInput
                        item={f}
                        state={formValues}
                        stateHandler={(newStateOrUpdater: any) => {
                          if (typeof newStateOrUpdater === 'function') {
                            setFormValues((prev: any) => {
                              const newState = newStateOrUpdater(prev);
                              if (f.id === 'colour' && newState.colour !== prev.colour) {
                                newState.colourName = newState.colour;
                              }
                              return newState;
                            });
                          } else {
                            const newState = { ...newStateOrUpdater };
                            if (f.id === 'colour' && newState.colour !== formValues.colour) {
                              newState.colourName = newState.colour;
                            }
                            setFormValues(newState);
                          }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              );
            })()}
          </Box>
        );
      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Product Images</Typography>
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Product Images* ({formValues.productImage.length} / 3 required)
              </Typography>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: '#f9f9f9',
                  '&:hover': { borderColor: '#1c75bc', bgcolor: '#f0f7ff' }
                }}
                onClick={() => document.getElementById('productImageInput')?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFileUpload(e.dataTransfer.files, 'productImage');
                }}
              >
                <Typography color="textSecondary">Drag and drop images here, or click to browse</Typography>
                <Typography variant="caption" color="textSecondary">Min. 3 images required</Typography>
                <input
                  id="productImageInput"
                  type="file"
                  multiple
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files, 'productImage')}
                />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
                {formValues.productImage.map((url: string, index: number) => (
                  <Box key={`${url}-${index}`} sx={{ position: 'relative', m: 1, border: '1px solid #ddd', borderRadius: 1, p: 0.5 }}>
                    <img
                      src={url}
                      alt="Product"
                      style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                    />
                    <Button
                      type="button"
                      variant="contained"
                      color="error"
                      size="small"
                      sx={{ position: 'absolute', top: -10, right: -10, minWidth: 24, width: 24, height: 24, p: 0, borderRadius: '50%' }}
                      onClick={() => removeImage(index)}
                    >
                      ×
                    </Button>
                  </Box>
                ))}
              </Box>

            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>Back Image</Typography>
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                  cursor: 'pointer',
                  bgcolor: '#f9f9f9',
                  '&:hover': { borderColor: '#1c75bc', bgcolor: '#f0f7ff' }
                }}
                onClick={() => document.getElementById('backImageInput')?.click()}
              >
                <Typography color="textSecondary">{formValues.backImage ? 'Change back image' : 'Click to upload back image'}</Typography>
                <input
                  id="backImageInput"
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files, 'backImage')}
                />
              </Box>
              {formValues.backImage && (
                <Box sx={{ mt: 2, display: 'inline-block', position: 'relative', border: '1px solid #ddd', borderRadius: 1, p: 0.5 }}>
                  <img
                    src={formValues.backImage}
                    alt="Back"
                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <Button
                    type="button"
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ position: 'absolute', top: -10, right: -10, minWidth: 24, width: 24, height: 24, p: 0, borderRadius: '50%' }}
                    onClick={() => setFormValues((prev: any) => ({ ...prev, backImage: '' }))}
                  >
                    ×
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid size={12} key={i}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, position: 'relative', pb: 10 }}>
      <BackNavigationButton onClick={() => navigate('/products')} />
      <Typography variant="h4" sx={{ mt: 2, mb: 4, fontWeight: 'bold' }}>
        Edit Product
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{
        mb: 4,
        '& .MuiStepIcon-root.Mui-active': { color: '#22c55e' },
        '& .MuiStepIcon-root.Mui-completed': { color: '#22c55e' }
      }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 4, mb: 3, minHeight: '400px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
          {renderStepContent(activeStep)}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <button
            type="button"
            disabled={activeStep === 0}
            onClick={handleBack}
            className={`px-8 py-2 border border-gray-300 rounded-lg transition duration-200 ${activeStep === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
          >
            Back
          </button>
          <Box>
            {activeStep === STEPS.length - 1 ? (
              <button
                type="submit"
                disabled={submitting}
                className="px-10 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-200 flex items-center justify-center min-w-[150px]"
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
              </button>
            ) : (
              <button
                type="submit"
                className="px-10 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-200"
              >
                Next
              </button>
            )}
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default EditProduct;
